import type { mileage } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Mileage screen derivations. PURE — no React, no query client — so the
 * filtering and the labels can be tested directly.
 *
 * The rule these functions defend: a reviewer's view must never imply it is
 * showing everything. A drive that is withheld is presented as withheld, and
 * the aggregate over withheld drives is stated so nobody is left thinking the
 * list is the whole picture.
 */

export type MileageTab = 'review' | 'work' | 'all';

/**
 * The rows for a tab.
 *
 * Every row here is already a work-classified drive: the server sends nothing
 * else to a reviewer. There is no "not yours to see" tab because there is no
 * longer anything of the worker's own to tab through.
 */
export function tripsFor(
  trips: readonly mileage.AdminTrip[],
  tab: MileageTab,
): mileage.AdminTrip[] {
  const rows = [...trips];
  if (tab === 'review') return rows.filter((t) => t.reviewState === 'ready_for_review');
  if (tab === 'work') return rows.filter((t) => t.classification === 'work');
  return rows;
}

/**
 * The state of a drive, as a badge.
 *
 * An excluded work drive is 'unsure' rather than 'urgent': policy excluded it,
 * nobody did anything wrong, and colouring it as a problem would put a red mark
 * against a worker who followed the rules.
 */
export function statusPill(trip: mileage.AdminTrip): { label: string; variant: Severity } {
  if (trip.classification === 'unclassified')
    return { label: 'Waiting on the worker', variant: 'plain' };
  if (trip.classification === 'personal') return { label: 'Personal', variant: 'plain' };
  if (trip.eligibility === 'excluded') return { label: 'Work — excluded', variant: 'unsure' };
  if (trip.reviewState === 'approved') return { label: 'Approved', variant: 'good' };
  if (trip.reviewState === 'needs_correction') return { label: 'Sent back', variant: 'look' };
  if (trip.reviewState === 'ready_for_review') return { label: 'Awaiting you', variant: 'look' };
  return { label: 'Work', variant: 'sky' };
}

/**
 * What an employer may know about driving nobody has answered for yet.
 *
 * A count and a distance, said out loud rather than left implicit, so a gap in
 * the reimbursement picture reads as "somebody still owes an answer" instead of
 * as missing data.
 *
 * WHAT THIS DELIBERATELY DOES NOT SAY: anything about personal drives. The
 * reviewer surface no longer receives them in any form, so there is no total to
 * report and nothing to infer from. Only UNANSWERED driving is counted, because
 * until a driver says what a drive was, nothing about it is private and
 * somebody has to ask.
 *
 * TOTAL BY CONSTRUCTION. This used to take the summary as read and dereference
 * `.count`, so a response without one took the whole Mileage page down with
 * "Cannot read properties of undefined (reading 'count')" -- the owner-reported
 * crash. A sentence-builder is the last thing that should be able to break a
 * screen: given nothing to say, it says nothing and the page renders.
 */
export function awaitingClassificationNote(
  awaiting?: { count: number; miles: number } | null,
): string | null {
  if (!awaiting || typeof awaiting.count !== 'number') return null;
  if (awaiting.count === 0) return 'Every drive here has been answered by its driver.';
  const drives = awaiting.count === 1 ? 'drive' : 'drives';
  const miles = typeof awaiting.miles === 'number' ? awaiting.miles : 0;
  const has = awaiting.count === 1 ? 'has' : 'have';
  return (
    `${awaiting.count} ${drives} (${miles.toFixed(1)} miles) ${has} not been classified yet. ` +
    'Until a driver says what a drive was, it is not reimbursable and you cannot see where it went.'
  );
}

/** Dollars from cents, for display only. Money is computed server-side. */
export function amountLabel(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * A rate boundary, as a day.
 *
 * `effectiveFrom` and `effectiveTo` are Postgres `date` columns and reach the
 * browser as bare calendar days ('2026-01-01'). A rate takes effect ON a day —
 * the same day for a reader in Minneapolis and one in Berlin — so it is
 * formatted in UTC, the zone the day string is anchored in.
 *
 * `new Date('2026-01-01')` is UTC midnight, which is 31 December in every zone
 * west of UTC; formatting that in the viewer's own zone would print the day
 * before the rate the administrator actually set. Pinning `timeZone: 'UTC'`
 * here is what stops that, and is the same treatment credential and supervision
 * requirement dates already get.
 */
export function rateDayLabel(day: string | null, locale?: string): string {
  if (!day) return '—';
  const d = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * How many location readings a drive's distance was measured from.
 *
 * A pure derivation with its own test, because it is the one place this screen
 * speaks about the route at all — and because `?? 0` is the trap. The API used
 * to send every reading and the screen printed `route.length`; it now sends a
 * COUNT and withholds the readings, so a drive built from four hundred of them
 * must still say four hundred. Rendering `0 readings` over a well-captured drive
 * would report a measurement failure that did not happen, which is the exact
 * shape of "a withheld value rendering as an absence".
 *
 * `undefined` is only ever a restricted drive, where the caller has already
 * branched on `detailRestricted` and this is not reached.
 */
export function readingsLabel(trip: Pick<mileage.AdminTrip, 'routePointCount'>): string {
  const n = trip.routePointCount;
  if (n == null) return 'Readings not shown';
  return `${n} ${n === 1 ? 'reading' : 'readings'}`;
}

/**
 * THE PAYROLL EXPORT, AND THE THREE THINGS THE SCREEN WAS NOT SAYING.
 *
 * `POST /mileage/exports` is the write that MOVES MONEY-SHAPED STATE: every
 * drive it sweeps goes `approved → exported`, its reimbursement row is stamped,
 * and neither can be swept again. The screen sent a hardcoded idempotency key —
 * `admin-${start}-${end}` — and its `onSuccess` discarded the `created` flag the
 * server returns beside the run (service.ts:2041).
 *
 * 1. A RE-RUN WAS INDISTINGUISHABLE FROM A RUN. Retrying the same key returns
 *    the EXISTING export with `created: false` and assembles nothing. The screen
 *    refreshed the history, a row appeared, and the operator read that row as
 *    the export they had just made.
 *
 * 2. WHICH MEANT A CORRECTION COULD NEVER BE PAID. `findApprovedReimbursementsInPeriod`
 *    selects `rb.status = 'approved' and t.review_state = 'approved'`
 *    (repository.ts:1382) — already-exported drives are excluded by
 *    construction. So a drive sent back, corrected and re-approved after the
 *    period was exported needed a SECOND run to reach payroll, and the fixed key
 *    made a second run impossible: it returned the first one, silently, forever.
 *    {@link mileageExportKey} is what makes the second run reachable, and it is
 *    safe precisely because of that `where` clause — a new key cannot re-pay a
 *    drive, only sweep one nobody has paid yet.
 *
 * 3. AND THERE IS STILL NO FILE. `GET /mileage/exports` returns the run's
 *    identity and totals; no route on this API produces a per-drive payroll
 *    artifact, and the `payroll` CSV in the reporting catalog is approved HOURS
 *    with "no pay rate or dollar amount". An operator who has just marked a
 *    fortnight of driving as paid should be told that in words rather than left
 *    looking for a download. See {@link EXPORT_HAS_NO_FILE_YET}.
 */

/**
 * The idempotency key for the NEXT run of a pay period.
 *
 * IDEMPOTENCY IS FOR A DOUBLE-CLICK, NOT FOR A DECISION. The server's unique
 * index exists so that one intent produces one export however many times the
 * request arrives; it was being used as a permanent lock on the period, which
 * is a different thing and not one anybody chose.
 *
 * So the key is derived from the runs the SERVER already holds for this period,
 * not from a counter in this browser: it survives a reload, two operators get
 * the same answer, and a double-click inside one render computes the same key
 * twice and is deduplicated exactly as before. The first run keeps the original
 * spelling so a period exported before this existed is still recognised as
 * exported.
 *
 * `priorRuns` is `undefined` when the history could not be read — and then the
 * honest key is the first one, because minting a fresh run off an unknown
 * history is how a period gets exported twice by somebody who could not see
 * that it already had been.
 */
export function mileageExportKey(
  payPeriodStart: string,
  payPeriodEnd: string,
  priorRuns: number | undefined,
): string {
  const base = `admin-${payPeriodStart}-${payPeriodEnd}`;
  return priorRuns === undefined || priorRuns === 0 ? base : `${base}-r${priorRuns + 1}`;
}

/**
 * How many runs the organization already holds for this pay period, or
 * `undefined` when the history has not been read.
 *
 * `undefined` rather than `0`: an unread history is not an unexported period,
 * and the difference decides both the key above and whether the screen may say
 * anything about what has already been run.
 */
export function mileageExportRunsFor(
  exports: readonly mileage.MileageExport[] | undefined,
  payPeriodStart: string,
  payPeriodEnd: string,
): number | undefined {
  if (!exports) return undefined;
  return exports.filter(
    (e) => e.payPeriodStart === payPeriodStart && e.payPeriodEnd === payPeriodEnd,
  ).length;
}

/** Stated on the card, because the absence is not obvious from a success message. */
export const EXPORT_HAS_NO_FILE_YET =
  'An export is a record inside Phibian — the period, the totals, and which drives were settled. ' +
  'There is no file to hand to payroll yet; nothing on this screen will download one, and the ' +
  'drives are marked paid either way.';

/**
 * What actually happened, as a sentence the operator can act on.
 *
 * TWO OUTCOMES, AND THEY MUST NOT LOOK ALIKE. `created: true` assembled a run;
 * `created: false` assembled NOTHING and handed back one that already existed.
 * The screen used to render the same silent refresh for both.
 *
 * `tripCount` IS ONLY ON THE FIRST OF THEM. `exportViewFrom` does not carry it,
 * and the create path adds `tripCount: claims.length` by hand (service.ts:2041)
 * — so a re-run answers with no count at all. Absent is not zero: "0 drives"
 * over a run that settled forty of them is the confident zero this codebase
 * treats as a defect, so the count is stated only where it exists.
 */
export function mileageExportOutcome(result: { export: mileage.MileageExport; created: boolean }): {
  created: boolean;
  message: string;
} {
  const e = result.export;
  const money = `${e.totalMiles.toFixed(1)} miles, ${amountLabel(e.totalCents)}`;
  if (result.created) {
    const drives =
      typeof e.tripCount === 'number'
        ? `${e.tripCount} ${e.tripCount === 1 ? 'drive' : 'drives'} — `
        : '';
    return {
      created: true,
      message:
        `Exported ${drives}${money}. Those drives are now marked exported and cannot be swept ` +
        'into another run.',
    };
  }
  return {
    created: false,
    message:
      `Nothing new was assembled. ${e.payPeriodStart} to ${e.payPeriodEnd} had already been ` +
      `exported, and the run shown covers ${money}. Any drive approved since then is still ` +
      'waiting — run the period again to sweep it; an already-exported drive cannot be paid twice.',
  };
}
